import React, { useCallback, useEffect, useRef, useState } from 'react';
import { prepareSchoolNoticePasteHtml } from '../../utils/schoolNoticeHtml';
import { PROGRAM_SLIDE_TEXT_FONT_OPTIONS } from '../../types/programContentSlideCanvas';

type ToolbarCmd =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'justifyLeft'
  | 'justifyCenter'
  | 'justifyRight'
  | 'insertUnorderedList'
  | 'insertOrderedList'
  | 'createLink'
  | 'removeFormat';

const NOTICE_FONT_OPTIONS = PROGRAM_SLIDE_TEXT_FONT_OPTIONS.map((opt) => ({
  value: opt.cssFamily,
  label: opt.label,
}));

export interface SchoolNoticeRichEditorProps {
  /** HTML fragment */
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  /** 접근성: `aria-labelledby`가 있으면 우선 사용 */
  ariaLabelledby?: string;
  /** 본문 영역 `aria-label` (labelledby 없을 때, 기본 `공지 본문`) */
  ariaLabel?: string;
  /** 본문 `data-placeholder` (기본: 공지 안내 문구) */
  placeholder?: string;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function selectNodeContents(sel: Selection, node: Node) {
  sel.removeAllRanges();
  const nr = document.createRange();
  nr.selectNodeContents(node);
  sel.addRange(nr);
}

function applyPartialStyle(
  el: HTMLElement,
  partial: { fontSize?: string; color?: string; fontFamily?: string }
) {
  if (partial.fontSize) el.style.fontSize = partial.fontSize;
  if (partial.color) el.style.color = partial.color;
  if (partial.fontFamily) el.style.fontFamily = partial.fontFamily;
}

/** 선택 범위와 겹치는 텍스트 노드 (문서 순서) */
function collectTextNodesInRange(root: HTMLElement, range: Range): Text[] {
  const out: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    const text = node as Text;
    if (text.length > 0) {
      try {
        if (range.intersectsNode(text)) out.push(text);
      } catch {
        /* ignore */
      }
    }
    node = walker.nextNode();
  }
  return out;
}

/**
 * 단일 텍스트 노드의 선택 구간에 서식 적용.
 * - 이미 span 한 겹에만 들어 있으면 그 span 스타일만 갱신
 * - 아니면 구간을 잘라 span으로 감쌈 (기존 중첩 인라인 서식도 덮어씀)
 */
function styleTextNodeInRange(
  textNode: Text,
  range: Range,
  partial: { fontSize?: string; color?: string; fontFamily?: string }
): HTMLElement | null {
  let from = 0;
  let to = textNode.length;
  if (range.startContainer === textNode) from = range.startOffset;
  if (range.endContainer === textNode) to = range.endOffset;
  from = Math.max(0, Math.min(from, textNode.length));
  to = Math.max(0, Math.min(to, textNode.length));
  if (from >= to) return null;

  const parent = textNode.parentElement;
  if (
    parent &&
    parent.tagName === 'SPAN' &&
    from === 0 &&
    to === textNode.length &&
    parent.childNodes.length === 1
  ) {
    applyPartialStyle(parent, partial);
    return parent;
  }

  let node: Text = textNode;
  if (to < node.length) node.splitText(to);
  if (from > 0) node = node.splitText(from);

  // split 후 단독 텍스트만 남은 span이면 그 스타일 갱신
  const p2 = node.parentElement;
  if (p2 && p2.tagName === 'SPAN' && p2.childNodes.length === 1) {
    applyPartialStyle(p2, partial);
    return p2;
  }

  const span = document.createElement('span');
  applyPartialStyle(span, partial);
  node.parentNode?.insertBefore(span, node);
  span.appendChild(node);
  return span;
}

function wrapSelectionWithSpanStyle(
  root: HTMLElement,
  partial: { fontSize?: string; color?: string; fontFamily?: string }
): boolean {
  root.focus();
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (range.collapsed) return false;
  if (!root.contains(range.commonAncestorContainer)) return false;

  const snapshot = range.cloneRange();
  const textNodes = collectTextNodesInRange(root, snapshot);
  if (textNodes.length === 0) return false;

  // 뒤에서부터 처리해 splitText가 앞쪽 오프셋을 깨지 않게 함
  const touched: HTMLElement[] = [];
  for (let i = textNodes.length - 1; i >= 0; i -= 1) {
    const el = styleTextNodeInRange(textNodes[i], snapshot, partial);
    if (el) touched.push(el);
  }
  if (touched.length === 0) return false;

  const first = touched[touched.length - 1];
  const last = touched[0];
  try {
    sel.removeAllRanges();
    const nr = document.createRange();
    nr.setStartBefore(first);
    nr.setEndAfter(last);
    sel.addRange(nr);
  } catch {
    selectNodeContents(sel, first);
  }
  return true;
}

/** LuckytemStudioClassLab(에듀코디) AdminPage 정렬 아이콘과 동일 스트로크 스타일 */
function IconAlignLeft() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M3 6h18M3 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function IconAlignCenter() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M3 6h18M6 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function IconAlignRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M3 6h18M9 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function IconListBullet() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function IconListOrdered() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M10 6h11M10 12h11M10 18h11M4 6h1v1H4zM4 12h1v1H4zM4 18h1v1H4z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function IconLink() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function IconLinkButton() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="7" width="18" height="10" rx="3" stroke="currentColor" strokeWidth="2" />
      <path d="M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function IconImage() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" />
      <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="2" />
      <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * LuckytemStudioClassLab(에듀코디) 공지 모달과 동일한 툴바·본문 테두리 패턴
 * (Toolbar + TermsModalEditor: 기본 연한 테두리, 본문 포커스 시 accent-strong)
 */
const SchoolNoticeRichEditor: React.FC<SchoolNoticeRichEditorProps> = ({
  value,
  onChange,
  disabled,
  ariaLabelledby,
  ariaLabel,
  placeholder = '공지 내용을 입력하세요...',
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const lastExternal = useRef<string | null>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const [fontPt, setFontPt] = useState(14);
  const fontPtRef = useRef(14);
  const [fontFamily, setFontFamily] = useState(NOTICE_FONT_OPTIONS[0].value);
  const fontFamilyRef = useRef(NOTICE_FONT_OPTIONS[0].value);
  const colorRef = useRef('#000000');

  const saveSelection = useCallback(() => {
    const el = rootRef.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) {
      savedRangeRef.current = null;
      return;
    }
    const range = sel.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) {
      savedRangeRef.current = null;
      return;
    }
    savedRangeRef.current = range.cloneRange();
  }, []);

  const restoreSelection = useCallback(() => {
    const el = rootRef.current;
    const range = savedRangeRef.current;
    if (!el || !range) return false;
    el.focus();
    const sel = window.getSelection();
    if (!sel) return false;
    try {
      sel.removeAllRanges();
      sel.addRange(range);
      return true;
    } catch {
      return false;
    }
  }, []);

  /** 툴바 버튼 클릭 시 contentEditable 선택이 풀리지 않도록 */
  const preserveSelectionOnToolbarMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (target.closest('button')) {
      e.preventDefault();
    }
  }, []);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    if (lastExternal.current === value) return;
    el.innerHTML = value || '';
    lastExternal.current = value;
  }, [value]);

  const emit = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    lastExternal.current = el.innerHTML;
    onChange(el.innerHTML);
  }, [onChange]);

  const exec = useCallback(
    (cmd: ToolbarCmd) => {
      if (disabled) return;
      const el = rootRef.current;
      if (!el) return;
      el.focus();
      if (cmd === 'createLink') {
        const url = window.prompt('링크 URL을 입력하세요.', 'https://');
        if (url) document.execCommand('createLink', false, url);
      } else {
        document.execCommand(cmd, false);
      }
      emit();
    },
    [disabled, emit]
  );

  const adjustFontPt = useCallback(
    (delta: number) => {
      if (disabled) return;
      const el = rootRef.current;
      if (!el) return;
      const next = Math.max(10, Math.min(28, fontPtRef.current + delta));
      fontPtRef.current = next;
      setFontPt(next);
      el.focus();
      const sel = window.getSelection();
      if (sel && !sel.isCollapsed) {
        const wrapped = wrapSelectionWithSpanStyle(el, { fontSize: `${next}px` });
        if (wrapped) emit();
      }
    },
    [disabled, emit]
  );

  const applyFontFamily = useCallback(
    (family: string) => {
      if (disabled) return;
      const el = rootRef.current;
      if (!el) return;
      fontFamilyRef.current = family;
      setFontFamily(family);
      restoreSelection();
      el.focus();
      const sel = window.getSelection();
      if (sel && !sel.isCollapsed) {
        if (wrapSelectionWithSpanStyle(el, { fontFamily: family })) emit();
      } else {
        document.execCommand('styleWithCSS', false, 'true');
        document.execCommand('fontName', false, family);
        emit();
      }
    },
    [disabled, emit, restoreSelection]
  );

  const onColorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      const el = rootRef.current;
      if (!el) return;
      const hex = e.target.value;
      colorRef.current = hex;
      restoreSelection();
      el.focus();
      const sel = window.getSelection();
      if (sel && !sel.isCollapsed) {
        if (wrapSelectionWithSpanStyle(el, { color: hex })) emit();
      } else {
        document.execCommand('styleWithCSS', false, 'true');
        document.execCommand('foreColor', false, hex);
        emit();
      }
    },
    [disabled, emit, restoreSelection]
  );

  const onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      e.preventDefault();
      const el = rootRef.current;
      if (!el) return;
      el.focus();

      const htmlRaw = e.clipboardData.getData('text/html')?.trim() ?? '';
      const plain = e.clipboardData.getData('text/plain') ?? '';

      let body = '';
      if (htmlRaw) {
        body = prepareSchoolNoticePasteHtml(htmlRaw);
      }
      if (!body.trim()) {
        body = escapeHtml(plain).replace(/\r\n|\r|\n/g, '<br>');
      }
      if (!body) return;

      const baseStyle = [
        `font-family:${fontFamilyRef.current}`,
        `font-size:${fontPtRef.current}px`,
        `color:${colorRef.current}`,
      ].join(';');
      // 툴바 서식을 기본으로 두고, 클립보드 HTML에 인라인 서식이 있으면 그대로 유지
      const toInsert = `<span style="${baseStyle}">${body}</span>`;

      document.execCommand('insertHTML', false, toInsert);
      emit();
    },
    [disabled, emit]
  );

  const insertImage = useCallback(() => {
    if (disabled) return;
    const el = rootRef.current;
    if (!el) return;
    const url = window.prompt('이미지 URL (https:// 로 시작)', 'https://');
    if (!url || !/^\s*https?:\/\//i.test(url.trim())) {
      if (url) window.alert('http(s) URL만 삽입할 수 있습니다.');
      return;
    }
    el.focus();
    document.execCommand('insertImage', false, url.trim());
    emit();
  }, [disabled, emit]);

  const insertLinkButton = useCallback(() => {
    if (disabled) return;
    const el = rootRef.current;
    if (!el) return;
    const labelRaw = window.prompt('버튼에 표시할 텍스트', '자세히 보기');
    if (labelRaw == null) return;
    const label = labelRaw.trim() || '자세히 보기';
    const urlRaw = window.prompt('링크 URL (https:// 로 시작)', 'https://');
    if (!urlRaw) return;
    const url = urlRaw.trim();
    if (!/^https?:\/\//i.test(url)) {
      window.alert('http(s) URL만 삽입할 수 있습니다.');
      return;
    }
    el.focus();
    const html = `<p style="text-align:center;margin:10px 0"><a class="cp-school-notice-link-btn" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a></p>`;
    document.execCommand('insertHTML', false, html);
    emit();
  }, [disabled, emit]);

  const a11yLabel = ariaLabel ?? '공지 본문';

  return (
    <div className={'cp-school-notice-editor' + (disabled ? ' is-disabled' : '')}>
      <div
        className="cp-school-notice-editor-toolbar"
        role="toolbar"
        aria-label="본문 서식"
        onMouseDown={preserveSelectionOnToolbarMouseDown}
      >
        <button type="button" className="cp-school-notice-editor-tb-btn" onClick={() => exec('bold')} disabled={disabled} title="굵게 (Ctrl+B)">
          <strong>B</strong>
        </button>
        <button type="button" className="cp-school-notice-editor-tb-btn" onClick={() => exec('italic')} disabled={disabled} title="기울임 (Ctrl+I)">
          <em>I</em>
        </button>
        <button type="button" className="cp-school-notice-editor-tb-btn" onClick={() => exec('underline')} disabled={disabled} title="밑줄 (Ctrl+U)">
          <u>U</u>
        </button>
        <span className="cp-school-notice-editor-tb-sep" aria-hidden />
        <button
          type="button"
          className="cp-school-notice-editor-tb-btn cp-school-notice-editor-tb-btn--icon"
          onClick={() => exec('justifyLeft')}
          disabled={disabled}
          title="왼쪽 정렬"
        >
          <IconAlignLeft />
        </button>
        <button
          type="button"
          className="cp-school-notice-editor-tb-btn cp-school-notice-editor-tb-btn--icon"
          onClick={() => exec('justifyCenter')}
          disabled={disabled}
          title="가운데 정렬"
        >
          <IconAlignCenter />
        </button>
        <button
          type="button"
          className="cp-school-notice-editor-tb-btn cp-school-notice-editor-tb-btn--icon"
          onClick={() => exec('justifyRight')}
          disabled={disabled}
          title="오른쪽 정렬"
        >
          <IconAlignRight />
        </button>
        <span className="cp-school-notice-editor-tb-sep" aria-hidden />
        <select
          className="cp-school-notice-editor-tb-font-family"
          value={fontFamily}
          disabled={disabled}
          title="글꼴"
          aria-label="글꼴"
          onMouseDown={() => saveSelection()}
          onChange={(e) => applyFontFamily(e.target.value)}
        >
          {NOTICE_FONT_OPTIONS.map((opt) => (
            <option key={opt.label} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <div className="cp-school-notice-editor-tb-font" role="group" aria-label="글자 크기">
          <span className="cp-school-notice-editor-tb-font-value">{fontPt}</span>
          <span className="cp-school-notice-editor-tb-font-unit">PT</span>
          <button type="button" className="cp-school-notice-editor-tb-font-step" onClick={() => adjustFontPt(1)} disabled={disabled} title="폰트 크기 증가">
            +
          </button>
          <button type="button" className="cp-school-notice-editor-tb-font-step" onClick={() => adjustFontPt(-1)} disabled={disabled} title="폰트 크기 감소">
            −
          </button>
        </div>
        <input
          type="color"
          className="cp-school-notice-editor-tb-color"
          defaultValue="#000000"
          onMouseDown={() => saveSelection()}
          onChange={onColorChange}
          disabled={disabled}
          title="텍스트 색상"
          aria-label="텍스트 색상"
        />
        <span className="cp-school-notice-editor-tb-sep" aria-hidden />
        <button type="button" className="cp-school-notice-editor-tb-btn cp-school-notice-editor-tb-btn--icon" onClick={() => exec('insertUnorderedList')} disabled={disabled} title="목록">
          <IconListBullet />
        </button>
        <button type="button" className="cp-school-notice-editor-tb-btn cp-school-notice-editor-tb-btn--icon" onClick={() => exec('insertOrderedList')} disabled={disabled} title="번호 목록">
          <IconListOrdered />
        </button>
        <span className="cp-school-notice-editor-tb-sep" aria-hidden />
        <button type="button" className="cp-school-notice-editor-tb-btn cp-school-notice-editor-tb-btn--icon" onClick={() => exec('createLink')} disabled={disabled} title="링크 삽입">
          <IconLink />
        </button>
        <button
          type="button"
          className="cp-school-notice-editor-tb-btn cp-school-notice-editor-tb-btn--icon"
          onClick={insertLinkButton}
          disabled={disabled}
          title="링크 버튼 삽입"
        >
          <IconLinkButton />
        </button>
        <button type="button" className="cp-school-notice-editor-tb-btn cp-school-notice-editor-tb-btn--icon" onClick={insertImage} disabled={disabled} title="이미지 삽입">
          <IconImage />
        </button>
        <span className="cp-school-notice-editor-tb-sep" aria-hidden />
        <button type="button" className="cp-school-notice-editor-tb-btn" onClick={() => exec('removeFormat')} disabled={disabled} title="서식 지우기">
          서식 지우기
        </button>
      </div>
      <div
        ref={rootRef}
        className="cp-school-notice-editor-field"
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabelledby ? undefined : a11yLabel}
        aria-labelledby={ariaLabelledby || undefined}
        data-placeholder={placeholder}
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
      />
    </div>
  );
};

export default SchoolNoticeRichEditor;
